<template>
    <div :class="$style['product-page']">
        <div :class="['content-width', $style['product-content']]">
            <h1 :class="$style['product-title']" v-if="!largeView">
                {{ product.name }}
            </h1>
            <media-gallery
                :value="media"
                :media-gallery-class="$style['media-gallery-container']"
                :full-width="!largeView"
                :vertical="largeView"
            >
                <template #under-image>
                    <div
                        :class="$style['desktop-wrapper-under-image']"
                        v-if="product.underImage != null"
                    >
                        <under-image :value="product.underImage"></under-image>
                    </div>
                </template>
                <template #after-first-image>
                    <product-image-overlays
                        :product="product"
                    ></product-image-overlays>
                </template>
            </media-gallery>
            <div :class="$style['specs-container']">
                <h1 :class="$style['product-title']" v-if="largeView">
                    {{ product.name }}
                </h1>
                <specs :product="product" :payment-keys="paymentKeys"></specs>
                <div
                    v-html="product.description"
                    v-if="!showDescriptionInOwnRow"
                    :class="$style['product-description']"
                ></div>
                <video-viewer
                    :class="[$style['video-viewer']]"
                    v-if="product.videoLink != null && !showDescriptionInOwnRow"
                    :video-link="product.videoLink"
                ></video-viewer>
            </div>
        </div>
        <div
            :class="[$style['mobile-wrapper-under-image'], 'content-width']"
            v-if="product.underImage != null"
        >
            <under-image :value="product.underImage"></under-image>
        </div>
        <div
            v-if="productHasStayInformation"
            :class="['content-width', $style['product-info-boxes']]"
        >
            <div
                :class="$style['product-info-box']"
                v-if="product.experienceInformation != null"
            >
                <strong>{{
                    product.experienceInformation.stayInclusionHeader
                }}</strong
                ><br />
                <ul :class="$style['product-info-list']">
                    <li
                        v-for="item in product.experienceInformation
                            .stayInclusions"
                    >
                        {{ item }}
                    </li>
                </ul>
            </div>
            <div
                :class="$style['product-info-box']"
                v-if="product.redeemInfo != null"
            >
                <strong>{{
                    labels.frontendProductpage.redeemInfoHeader
                }}</strong
                ><br />
                <ul :class="$style['product-info-list']">
                    <li
                        v-for="item in product.redeemInfo
                            .replace(/\r/g, '')
                            .split(/(^|\n)\s*[\*-\>●]\s*/g)
                            .filter((x) => x.trim().length > 0)"
                    >
                        {{ item }}
                    </li>
                </ul>
            </div>
        </div>
        <div
            :class="[
                $style['product-description-row'],
                product.nextToDescriptionImage != null ? 'content-width' : '',
            ]"
        >
            <div
                v-if="showDescriptionInOwnRow"
                v-html="product.description"
                :class="[
                    'content-width',
                    $style['product-description'],
                    product.nextToDescriptionImage != null
                        ? $style['product-info-box-base']
                        : '',
                    product.nextToDescriptionImage != null
                        ? $style['description-next-to-image']
                        : '',
                ]"
            ></div>
            <div
                v-if="product.nextToDescriptionImage != null"
                :class="[
                    $style['product-info-box-base'],
                    $style['next-to-desc-image-container'],
                ]"
            >
                <NextToDescriptionImage
                    :value="product.nextToDescriptionImage"
                ></NextToDescriptionImage>
            </div>
            <video-viewer
                :class="['content-width', $style['video-viewer']]"
                v-if="product.videoLink != null && showDescriptionInOwnRow"
                :video-link="product.videoLink"
            ></video-viewer>
        </div>
        <usp v-if="product.isStockProduct === true"></usp>
        <div
            v-if="product.brandInfo != null"
            :class="['content-width', $style['brand-info-comp']]"
        >
            <brand-info :brand-info="product.brandInfo"></brand-info>
        </div>
        <div :class="['content-width', $style['related-info']]">
            <related-products :product="product"></related-products>
        </div>
    </div>
</template>

<style module lang="scss">
@import "../../../../responsive/style.scss";

.video-viewer {
    margin-top: 1em;
}

.product-title {
    font-size: 24px;
    text-align: center;
    color: #3f3f3f;
    margin-bottom: 12px;
    line-height: 1em;
}

.brand-info-comp {
    padding-bottom: 0;
}

.related-info {
    padding-bottom: 0;
}

.product-content {
    display: flex;
    align-items: start;
    justify-content: stretch;
    flex-direction: column;
}

.specs-container {
    margin-top: 15px;
    width: 100%;
}

.product-description {
    color: #666666;
    font-family: "Open Sans";
    font-size: 13px;
}

.product-description > p:first-child,
.product-description > *:first-child > p:first-child,
.product-description > *:first-child > *:first-child > p:first-child,
.product-description
    > *:first-child
    > *:first-child
    > *:first-child
    > p:first-child,
.product-description
    > *:first-child
    > *:first-child
    > *:first-child
    > *:first-child
    > p:first-child {
    margin-top: 0;
}

.product-description a {
    text-decoration: underline !important;
}

.desktop-wrapper-under-image {
    display: none;
    max-width: 400px;
}

.mobile-wrapper-under-image {
    margin-top: 40px;
    display: block;
}

$infoboxSpacing: 1rem;

.product-info-boxes {
    display: flex;
    flex-direction: column;
    margin-top: $infoboxSpacing;
    margin-bottom: $infoboxSpacing;
    font-size: 13px;
}

.product-description-row {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
}

.next-to-desc-image-container {
    order: -1;
    padding: 0 !important;
}

.product-info-box-base {
    color: #37465a;
    padding: 15px;
}
.product-info-box {
    @extend .product-info-box-base;
    background-color: rgb(246, 242, 230);
}

.product-info-list {
    list-style-type: "- ";
    padding-left: 10px;
    margin-top: 0.25rem;
}

.product-info-box + .product-info-box {
    margin-top: $infoboxSpacing;
}

.description-next-to-image {
    padding: 0 !important;
    margin-top: $infoboxSpacing;
}

@include media-breakpoint-up(lg) {
    .desktop-wrapper-under-image {
        display: block;
    }

    .mobile-wrapper-under-image {
        display: none;
    }

    .product-description-row {
        flex-direction: row;
    }

    .next-to-desc-image-container {
        order: initial;
        flex: 1;
        flex-basis: calc(50% - $infoboxSpacing);
        margin-left: $infoboxSpacing;
    }

    .description-next-to-image {
        margin-top: 0;
        flex: 1;
        flex-basis: calc(50% - $infoboxSpacing);
    }

    .product-title {
        font-size: 25px;
        margin-top: 0;
        margin-bottom: 20px;
        line-height: 1em;
        text-align: left;
    }

    .product-content {
        flex-direction: row;
    }

    .media-gallery-container {
        flex: 0;
    }

    .specs-container {
        margin-left: 20px;
        margin-top: 0.125rem;
        flex: 1;
    }

    .product-info-boxes {
        flex-direction: row;
        align-items: stretch;
        justify-content: stretch;
    }

    .product-info-box {
        flex: 1;
    }
    .product-info-box + .product-info-box {
        margin-top: 0px;
        margin-left: $infoboxSpacing;
    }
}
</style>

<script setup lang="ts">
import mediaGallery from "../media-gallery/MediaGalleryMain.vue";
import specs from "./specs.vue";
import usp from "../../../content/usp/usp.vue";
import brandInfo from "./brand-info.vue";
import relatedProducts from "./related-products.vue";
import UnderImage from "./under-image.vue";
import { computed, watch, type PropType, ref } from "vue";
import { type ShopProduct } from "./types";
import labels from "../../shared/labels";
import ProductImageOverlays from "../common/product-image-overlays.vue";
import VideoViewer from "../common/video-link/video-viewer.vue";
import { getBreakpointObservable } from "../../../../shared/breakpoint-observable";
import {
    type PaymentMethodKey,
    type PaymentLogoKey,
} from "../../checkout/payment-type/payment-method";
import { registerVueProductCountdown } from "../../../../utilities/countdown";
import { isValidSecondsleft } from "../../../commons/product/auction/countdown";
import NextToDescriptionImage from "./next-to-description-image.vue";

const breakpointObserver = getBreakpointObservable();

const props = defineProps({
    product: {
        type: Object as PropType<ShopProduct>,
        required: true,
    },
    paymentKeys: Array as PropType<(PaymentLogoKey | PaymentMethodKey)[]>,
});

const currentBreakpoint = computed(() => {
    return breakpointObserver.currentBreakpoint;
});
const largeView = computed(() => {
    return currentBreakpoint.value?.up("lg") ?? false;
});
const showDescriptionInOwnRow = computed(() => {
    return props.product.stock == null && !props.product.isStockProduct;
});
const productHasStayInformation = computed(() => {
    return (
        props.product.experienceInformation != null &&
        props.product.experienceInformation.stayInclusions.length > 0
    );
});

import fallbackImage from "../fallback-image.jpg?file";
import { type MediaItem } from "../media-gallery/types";
const media = computed(() => {
    if (props.product.media != null && props.product.media.length > 0)
        return props.product.media;
    const res: MediaItem[] = [
        {
            src: fallbackImage,
            alt: "Billede mangler",
            type: "picture",
            size: {
                width: 470,
                height: 353,
            },
            sources: [],
            srcset: "",
            thumbnail: fallbackImage,
        },
    ];
    return res;
});

const initializedCountdown = ref(false);
const initializedDate = ref<Date>();
const initialSeconds = ref<number>();

watch(
    () => props.product.timeTotalSeconds,
    (n) => {
        if (initializedCountdown.value) return;
        if (!isValidSecondsleft(n)) return;
        initializedCountdown.value = true;
        registerVueProductCountdown({
            itemId: props.product.id,
            secondsGetter: () => props.product.timeTotalSeconds,
            secondsSetter: (v) => {
                props.product.timeTotalSeconds = v;
            },
            getInitializedDate: () => initializedDate.value,
            getInitialSeconds: () => initialSeconds.value,
            setInitializedDate: (v) => (initializedDate.value = v),
            setInitialSeconds: (v) => (initialSeconds.value = v),
        });
    },
    {
        immediate: true,
    }
);
</script>
